import React from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from "./button";
import { CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import Carousel from './Carousel';

const NotificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get type from navigation state or URL parameter, default to 'success'
  const type = location.state?.type || searchParams.get('type') || 'success';

  const handleOkClick = () => {
    // Navigate back to login page
    navigate('/');
  };

  return (
    <section className="h-screen bg-white flex flex-col">
      <Header />
      <div className="flex flex-1 overflow-hidden bg-white">
        <div className="flex w-full h-full">
          {/* Left Panel - Carousel */}
          <Carousel />
          
          {/* Right Panel - Notification */}
          <div className="w-full lg:w-1/2 flex justify-center p-8 bg-white overflow-y-auto max-h-full">
            <div className="w-full max-w-md py-4">
              
              {/* Notification Content */}
              <div className={`p-8 rounded-lg border ${
                type === 'success' 
                  ? 'bg-white border-gray-200 shadow-sm' 
                  : type === 'error'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                {type === 'success' ? (
                  /* Success notification with specific design */
                  <div className="text-center">
                    <div className="mb-6">
                      <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                        Your access request has been submitted successfully.
                      </h2>
                    </div>
                    
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4 text-left">
                        Next Step:
                      </h3>
                      <ul className="text-left space-y-3 text-gray-600">
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>You'll receive an email confirmation shortly</span>
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>Our team will review your request within 1-2 business days</span>
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>You’ll receive an email with your access details as soon as your request is approved</span>
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>Access your instance from the dashboard after approval</span>
                        </li>
                      </ul>
                    </div>
                    
                    <Button
                      onClick={handleOkClick}
                      className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      OK
                    </Button>
                  </div>
                ) : type === 'warning' ? (
                  /* Warning notification with same design as success */
                  <div className="text-center">
                    <div className="mb-6">
                      <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                        Account Creation Request Submitted!
                      </h2>
                      <p className="text-gray-500 text-base">
                        Your request has been received. However, there was an issue with our notification system.
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4 text-left">
                        What Happened:
                      </h3>
                      <ul className="text-left space-y-3 text-gray-600">
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>❌ Teams webhook temporarily unavailable</span>
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>Our team will still review your request</span>
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>You will be contacted at your email address</span>
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>Please contact support if this issue persists</span>
                        </li>
                      </ul>
                    </div>
                    
                    <Button
                      onClick={handleOkClick}
                      className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      OK
                    </Button>
                  </div>
                ) : (
                  /* Error notification with same design as success */
                  <div className="text-center">
                    <div className="mb-6">
                      <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                        Submission Error
                      </h2>
                      <p className="text-gray-500 text-base">
                        An error occurred while submitting your request. Please try again later.
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4 text-left">
                        What You Can Do:
                      </h3>
                      <ul className="text-left space-y-3 text-gray-600">
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>❌ Network connection failed</span>
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>Please check your internet connection</span>
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>Try submitting the form again</span>
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>Contact support if the problem persists</span>
                        </li>
                      </ul>
                    </div>
                    
                    <Button
                      onClick={handleOkClick}
                      className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      OK
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </section>
  );
};

export default NotificationPage;